import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import { ChatRequestModel, IChatRequestModel, ChatMessageModel } from '../client/SmartAssistantBackendClient';
import { ClientHelper } from '../client/CreateNswagClient';
import { buildRequestHistory } from '../utils/chatBuildRequest';
import { mergeResponseHistory } from '../utils/chatResponseMerge';

type SendMessageInternalParams = {
  chatId: string | null;
  history: ChatMessageModel[];
  createChatId: () => string;
  setChatId: (id: string) => void;
  setHistory: (value: ChatMessageModel[]) => void;
  setChatBuffer: (chatId: string, messages: ChatMessageModel[]) => void;
  setAwaitingResponse: (value: boolean) => void;
  setSubmittedClarifications: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  setClarificationOpen: (value: boolean) => void;
  setText: (value: string) => void;
  loadFrontendConfig: (token: string) => Promise<void>;
  updateLocalHistory: (chatId: string, messages: ChatMessageModel[]) => void;
  signalRApiRef: MutableRefObject<{
    stopAllConnections?: (exceptId?: string) => Promise<void>;
    connectSignalR?: (consumer: any, chatKey: string) => Promise<void>;
  } | null>;
};

export type SendMessageInternalResult = {
  appended: ChatMessageModel[];
  responseTurn: ChatMessageModel[];
  resolvedChatId: string;
};

export const useSendMessageInternal = (params: SendMessageInternalParams) => {
  return useCallback(async (consumer: any, payload: {
    text: string;
    assistantInternalName: string;
    promptKey: string | null;
    includeProfile: boolean;
    clarification?: any;
    chatIdOverride?: string;
    appendLocalUserMessage: boolean;
    stopSpeech: boolean;
  }, sendEmptyClarification: boolean = false): Promise<SendMessageInternalResult> => {
    params.setAwaitingResponse(true);
    try {
      const token = await consumer.getUserToken();
      const client = ClientHelper.createNswagClient(token as string);
      await params.loadFrontendConfig(token as string);

      const nextChatId = payload.chatIdOverride ?? params.chatId ?? params.createChatId();
      if (!params.chatId && !payload.chatIdOverride) {
        params.setChatId(nextChatId);
      }

      const trimmedText = payload.text.trim();
      const clarificationPayload = payload.clarification ?? undefined;
      const { normalizedOutgoing, requestHistory } = buildRequestHistory({
        history: params.history,
        trimmedText,
        clarificationPayload,
        appendLocalUserMessage: payload.appendLocalUserMessage,
      });
      const appended = payload.appendLocalUserMessage
        ? normalizedOutgoing.slice(params.history.length)
        : [];
      if (payload.appendLocalUserMessage) {
        params.updateLocalHistory(nextChatId, normalizedOutgoing);
      }

      const requestMessage: IChatRequestModel = {
        history: requestHistory,
        userMessage: trimmedText || undefined,
        chatId: nextChatId,
        clarification: clarificationPayload,
        assistantInternalName: payload.assistantInternalName,
        promptConfigKey: payload.promptKey || undefined,
        includeProfile: payload.includeProfile,
      };

      await params.signalRApiRef.current?.stopAllConnections?.(nextChatId);
      await params.signalRApiRef.current?.connectSignalR?.(consumer, nextChatId);

      const response = await client.post(new ChatRequestModel(requestMessage));
      const responseTurn = response.currentTurnMessages?.length
        ? response.currentTurnMessages.map((m: any) => ({ ...m })) : [];
      let mergedHistory = mergeResponseHistory({
        normalizedOutgoing,
        responseTurn: responseTurn as ChatMessageModel[],
      });
      params.setHistory(mergedHistory);
      const resolvedChatId = response.chatId && response.chatId !== nextChatId ? response.chatId : nextChatId;
      params.setChatBuffer(resolvedChatId, mergedHistory);
      params.setAwaitingResponse(false);
      if (response.chatId && response.chatId !== nextChatId) {
        params.setChatId(response.chatId);
        params.signalRApiRef.current?.connectSignalR?.(consumer, response.chatId);
      }

      params.setText('');
      if (clarificationPayload?.messageId) {
        params.setSubmittedClarifications((prev: any) => ({ ...prev, [clarificationPayload.messageId as string]: true }));
        params.setClarificationOpen(false);
      }
      return {
        appended,
        responseTurn: responseTurn as ChatMessageModel[],
        resolvedChatId,
      };
    } catch (err: any) {
      console.error('Chat senden fehlgeschlagen', err);
      alert(err?.message ?? 'Die Anfrage konnte nicht gesendet werden (Timeout oder Fehler im Backend). Bitte erneut versuchen.');
      params.setAwaitingResponse(false);
      return {
        appended: [],
        responseTurn: [],
        resolvedChatId: payload.chatIdOverride ?? params.chatId ?? params.createChatId(),
      };
    }
  }, [params]);
};
